import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Select, Textarea } from '@components/ui';
import { CTA, DASHED_RULE_STYLE, FIELD, GHOST, SECTION_NUM } from '@components/ui/designTokens';
import type { Product } from '../types/product.types';
import { SEGMENTS, getFamiliesBySegment } from '../constants/taxCategories';
import { SKU_REGEX, SKU_MAX_LENGTH, NO_HTML_NAME_REGEX } from '@/utils/businessValidation';

/**
 * Formulario de producto — Claude Design · Productos.dc.html
 *
 * Solo presentacion. Las tres filas sueltas del formulario original pasan a ser
 * secciones numeradas 01 / 02 / 03 con regla dasheada al ancho restante, que es
 * el vocabulario del sistema para dar estructura dentro de una superficie sin
 * meter mas bordes. Ningun campo, etiqueta, opcion ni validacion cambio: siguen
 * los mismos 11 controles, los mismos textos y el mismo esquema de zod.
 *
 * Se mantienen los componentes compartidos Input / Select / Textarea para no
 * perder el contador de caracteres, el mensaje de error ni el ref de
 * react-hook-form; el look nuevo entra por `className` (cn usa tailwind-merge,
 * asi que las utilidades en conflicto se reemplazan en vez de acumularse).
 */

/** Encabezado de seccion: numero monoespaciado + titulo + regla dasheada. */
function SectionHead({ index, title }: { index: string; title: string }) {
  return (
    <div className="mb-4 flex items-center gap-3.5">
      <span className={`${SECTION_NUM} tabular-nums`}>{index}</span>
      <span className="text-[14px] font-bold tracking-[-0.015em] text-ink-850 dark:text-white">
        {title}
      </span>
      <span className="h-px flex-1" style={DASHED_RULE_STYLE} aria-hidden="true" />
    </div>
  );
}

const productSchema = z.object({
  code: z.string()
    .min(1, 'El código es requerido')
    .max(SKU_MAX_LENGTH, `Máximo ${SKU_MAX_LENGTH} caracteres`)
    .regex(SKU_REGEX, 'El SKU contiene caracteres no permitidos'),
  name: z.string()
    .min(2, 'Mínimo 2 caracteres')
    .max(500, 'Máximo 500 caracteres')
    .regex(NO_HTML_NAME_REGEX, 'No se permiten etiquetas HTML ni espacios al inicio/final'),
  description: z.string().min(2, 'Mínimo 2 caracteres').max(500, 'Máximo 500 caracteres'),
  unitPrice: z.number({
    required_error: 'El precio es requerido',
    invalid_type_error: 'El precio debe ser un número válido',
  }).min(0, 'El precio debe ser mayor o igual a 0'),
  taxRate: z.number({
    invalid_type_error: 'La tasa de impuesto debe ser un número válido',
  }).min(0, 'La tasa de impuesto debe ser mayor o igual a 0').max(100, 'La tasa de impuesto debe estar entre 0 y 100'),
  category: z.string().min(1, 'La categoría es requerida'),
  unit: z.string().min(1, 'La unidad de medida es requerida'),
  segment: z.string().optional(),
  family: z.string().optional(),
  stock: z.number({
    invalid_type_error: 'El stock debe ser un número válido',
  }).min(0, 'El stock debe ser mayor o igual a 0').optional(),
  isActive: z.boolean(),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductFormProps {
  product?: Product;
  onSubmit: (data: Partial<Product>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ProductForm({ product, onSubmit, onCancel, isLoading }: ProductFormProps) {
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [availableFamilies, setAvailableFamilies] = useState<any[]>([]);
  const [priceDisplay, setPriceDisplay] = useState<string>(
    product?.price != null ? product.price.toFixed(2) : '0.00'
  );

  // Map Product type to ProductFormData
  const getDefaultValues = (): Partial<ProductFormData> => {
    if (!product) {
      return {
        unitPrice: 0,
        taxRate: 7,
        isActive: true,
        stock: 0,
        segment: '',
        family: '',
        unit: 'und',
      };
    }

    // Extract numeric tax rate from taxCode (e.g., "ITBMS7" -> 7, "IGV18" -> 18, "NOTAX" -> 0)
    const extractTaxRate = (taxCode?: string): number => {
      if (!taxCode) return 7;
      if (taxCode.toUpperCase() === 'NOTAX') return 0;
      const match = taxCode.match(/\d+/);
      return match ? parseInt(match[0], 10) : 7;
    };

    return {
      code: product.sku,
      name: product.name,
      description: product.description || '',
      unitPrice: product.price != null ? Math.round(product.price * 100) / 100 : 0,
      taxRate: extractTaxRate(product.taxCode),
      category: product.category || '',
      unit: product.unitCode || 'und',
      segment: product.segment || '',
      family: product.family || '',
      stock: 0, // Stock is not in Product type from backend
      isActive: !product.isDeleted,
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  // Watch segment changes
  const segmentValue = watch('segment');

  // Reset form values when product changes (for edit mode)
  useEffect(() => {
    const defaultValues = getDefaultValues();
    reset(defaultValues);
    setPriceDisplay(product?.price != null ? product.price.toFixed(2) : '0.00');

    // Initialize segment and families for editing
    if (product && product.segment) {
      setSelectedSegment(product.segment);
      const families = getFamiliesBySegment(product.segment);
      setAvailableFamilies(families);
    } else {
      setSelectedSegment('');
      setAvailableFamilies([]);
    }
  }, [product, reset]);

  // Update families when segment changes
  useEffect(() => {
    if (segmentValue) {
      const families = getFamiliesBySegment(segmentValue);
      setAvailableFamilies(families);
      setSelectedSegment(segmentValue);
    } else {
      setAvailableFamilies([]);
      setValue('family', '');
    }
  }, [segmentValue, setValue]);

  // Transform ProductFormData to Product format
  const handleFormSubmit = (data: ProductFormData) => {
    const productData: Partial<Product> = {
      sku: data.code,
      name: data.name,
      description: data.description,
      price: data.unitPrice,
      taxCode: data.taxRate === 0 ? 'NOTAX' : `ITBMS${data.taxRate}`,
      category: data.category,
      unitCode: data.unit,
      segment: data.segment || undefined,
      family: data.family || undefined,
      isDeleted: !data.isActive,
      isService: data.category === 'Servicios',
      priceIncludesTax: false,
      currency: 'USD',
    };

    console.log('📤 Product Form Submit Data:', {
      ...productData,
      hasSegment: !!data.segment,
      hasFamily: !!data.family,
      segmentValue: data.segment,
      familyValue: data.family,
    });

    onSubmit(productData);
  };

  return (
    /*
      ModalBody se abre en modo `h-auto max-h-[80vh] overflow-y-auto` desde
      ProductListPage, asi que el formulario fluye natural y el scroll queda a
      cargo del modal — el pie va al final del flujo, no anclado.
    */
    <form onSubmit={handleSubmit(handleFormSubmit)} className="font-display" data-testid="product-form-form">
      {/* ===== 01 · Identificación ===== */}
      <SectionHead index="01" title="Identificación" />

      {/*
        Grilla asimetrica: el codigo es corto y de ancho fijo, el nombre es el
        campo largo. La original repartia 1fr/1fr/1fr y dejaba el nombre igual
        de angosto que el codigo.
      */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[150px_190px_minmax(0,1fr)]">
        <Input
          label="Código *"
          placeholder="PROD-001"
          error={errors.code?.message}
          maxLength={SKU_MAX_LENGTH}
          className={FIELD}
          {...register('code')}
         data-testid="product-form-code-input"/>
        <Select
          label="Categoría *"
          error={errors.category?.message}
          className={FIELD}
          options={[
            { value: 'Servicios', label: 'Servicios' },
            { value: 'Productos', label: 'Productos' },
            { value: 'Software', label: 'Software' },
            { value: 'Consultoría', label: 'Consultoría' },
            { value: 'Hardware', label: 'Hardware' },
            { value: 'Otro', label: 'Otro' },
          ]}
          {...register('category')}
         data-testid="product-form-category-select"/>
        <Input
          label="Nombre del Producto *"
          placeholder="Nombre del producto"
          error={errors.name?.message}
          maxLength={500}
          className={FIELD}
          {...register('name')}
         data-testid="product-form-name-input"/>
      </div>

      <div className="mt-4">
        <Textarea
          label="Descripción *"
          placeholder="Descripción del producto o servicio"
          error={errors.description?.message}
          maxLength={500}
          rows={2}
          className={FIELD}
          {...register('description')}
         data-testid="product-form-description-textarea"/>
      </div>

      {/* ===== 02 · Precio y medida ===== */}
      <div className="mt-7">
        <SectionHead index="02" title="Precio y medida" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Input
          label="Precio *"
          type="text"
          inputMode="decimal"
          placeholder="0.00"
          value={priceDisplay}
          onChange={(e) => {
            const raw = e.target.value;
            if (/^[0-9]*\.?[0-9]*$/.test(raw) || raw === '') {
              setPriceDisplay(raw);
              const num = parseFloat(raw);
              setValue('unitPrice', isNaN(num) ? (undefined as any) : num, { shouldValidate: true });
            }
          }}
          onBlur={() => {
            const num = parseFloat(priceDisplay);
            if (!isNaN(num) && num >= 0) {
              setPriceDisplay(num.toFixed(2));
              setValue('unitPrice', num, { shouldValidate: true });
            } else {
              setPriceDisplay('');
              setValue('unitPrice', undefined as any, { shouldValidate: true });
            }
          }}
          error={errors.unitPrice?.message}
          className={`${FIELD} text-right font-mono tabular-nums`}
         data-testid="product-form-price-input"/>
        <Select
          label="Impuesto *"
          error={errors.taxRate?.message}
          className={FIELD}
          options={[
            { value: '0', label: '0%' },
            { value: '7', label: '7%' },
            { value: '10', label: '10%' },
            { value: '15', label: '15%' },
          ]}
          {...register('taxRate', { valueAsNumber: true })}
         data-testid="product-form-tax-rate-select"/>
        <Select
          label="Unidad *"
          error={errors.unit?.message}
          className={FIELD}
          options={[
            { value: 'und', label: 'Unidad' },
            { value: 'Persona', label: 'Persona' },
            { value: 'Grupo', label: 'Grupo' },
            { value: 'Paquete', label: 'Paquete' },
            { value: 'Kit', label: 'Kit' },
            { value: 'Caja', label: 'Caja' },
            { value: 'Docena', label: 'Docena' },
            { value: 'Viaje', label: 'Viaje' },
            { value: 'Visita', label: 'Visita' },
            { value: 'Hora', label: 'Hora' },
            { value: 'Dia', label: 'Día' },
            { value: 'Semana', label: 'Semana' },
            { value: 'Mes', label: 'Mes' },
            { value: 'Ano', label: 'Año' },
            { value: 'Actividad', label: 'Actividad' },
            { value: 'Hora trabajo', label: 'Hora de trabajo' },
            { value: 'g', label: 'Gramos (g)' },
            { value: 'kg', label: 'Kilogramos (kg)' },
            { value: 'lb', label: 'Libras (lb)' },
            { value: 'oz', label: 'Onzas (oz)' },
            { value: 't', label: 'Tonelada (t)' },
            { value: 'cm', label: 'Centímetros (cm)' },
            { value: 'm', label: 'Metros (m)' },
            { value: 'km', label: 'Kilómetros (km)' },
            { value: 'm2', label: 'M² (m2)' },
            { value: 'ha', label: 'Hectárea (ha)' },
            { value: 'ml', label: 'Mililitros (ml)' },
            { value: 'l', label: 'Litros (l)' },
            { value: 'm3', label: 'M³ (m3)' },
            { value: 'gal', label: 'Galón (gal)' },
            { value: 'kWh', label: 'Kilowatt hora (kWh)' },
          ]}
          {...register('unit')}
         data-testid="product-form-unit-select"/>
        <Input
          label="Stock"
          type="number"
          placeholder="0"
          error={errors.stock?.message}
          className={`${FIELD} text-right font-mono tabular-nums`}
          {...register('stock', { valueAsNumber: true })}
         data-testid="product-form-stock-input"/>
      </div>

      {/* ===== 03 · Clasificación fiscal ===== */}
      <div className="mt-7">
        <SectionHead index="03" title="Clasificación fiscal" />
      </div>

      {/*
        Segmento y Familia son la cadena UNSPSC y se leen de izquierda a
        derecha, asi que van juntos en una fila propia. El estado del producto
        no pertenece a esta clasificacion y salio de aca: era la tercera columna
        de la grilla original solo para rellenar el hueco.
      */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Select
          label="Segmento (Opcional)"
          error={errors.segment?.message}
          className={FIELD}
          {...register('segment')} data-testid="product-form-segment-select"
        >
          <option value="">Seleccione un segmento</option>
          {SEGMENTS.map((segment) => (
            <option key={segment.Code} value={segment.Code.toString()}>
              {segment.Code} - {segment.Description}
            </option>
          ))}
        </Select>

        <Select
          label="Familia (Opcional)"
          error={errors.family?.message}
          disabled={!selectedSegment}
          className={FIELD}
          {...register('family')} data-testid="product-form-family-select"
        >
          <option value="">
            {selectedSegment ? 'Seleccione una familia' : 'Primero seleccione segmento'}
          </option>
          {availableFamilies.map((family) => (
            <option key={family.Code} value={family.Code}>
              {family.Code} - {family.Description}
            </option>
          ))}
        </Select>
      </div>

      {/* Estado: fila propia con explicacion, no un checkbox suelto al pie de una columna */}
      <label
        htmlFor="isActive"
        className="mt-5 flex cursor-pointer items-center gap-3 rounded-xl border border-[#ececec] px-4 py-3.5 transition-colors hover:bg-[#fafafa] dark:border-secondary-600 dark:hover:bg-secondary-700/40"
      >
        <input
          type="checkbox"
          id="isActive"
          className="h-4 w-4 rounded border-secondary-300 text-[#0d9488] focus:ring-teal-500/40"
          {...register('isActive')}
         data-testid="product-form-active-checkbox"/>
        <span className="text-[14px] font-semibold text-ink-850 dark:text-white">Producto Activo</span>
        <span className="ml-auto font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-secondary-400">
          Estado
        </span>
      </label>

      {/*
        Botones. Van en la banda de pie anclada del diseño: se pegan al borde
        inferior del modal (sticky) y sangran hasta los costados con los
        margenes negativos que compensan el padding de <ModalBody>, para que la
        accion principal siga visible aunque el formulario haga scroll.
      */}
      <div className="sticky -bottom-7 -mx-9 -mb-7 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#f0f0f0] bg-[#fafafa] px-9 py-[18px] dark:border-secondary-700 dark:bg-secondary-900/95">
        <div className="flex items-center gap-2 text-[13px] text-secondary-500 dark:text-secondary-400">
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#fada51] shadow-[0_0_6px_rgba(250,218,81,.6)]"
          />
          <span>
            Los campos <span className="font-bold text-[#b8503a]">*</span> son obligatorios
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading} className={GHOST} data-testid="product-form-cancel-button">
            Cancelar
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading} className={CTA} data-testid="product-form-submit-button">
            {product ? 'Actualizar' : 'Crear'} Producto
          </Button>
        </div>
      </div>
    </form>
  );
}
